import { useState } from 'react';
import {
  DevHubAvatar,
  DevHubChip,
  DevHubGrid,
  DevHubListItemText,
  DevHubPaper,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Device } from '../types/InsightsTypes';
import {
  ResponsibleParty,
  RolesQueryVariables,
  UserRoleInfo,
  RoleId,
} from '../types/RoleTypes';
import { DocumentNode } from 'graphql/index';
import { DeleteResponsibility } from './DeleteResponsibility';

interface Props {
  device?: Device;
  appId: number;
  responsibilityId?: number;
  party: ResponsibleParty;
  rolesQuery: DocumentNode;
  rolesVariables: RolesQueryVariables;
  roles: UserRoleInfo[];
  showDeleteIcon?: boolean;
  showDisabled?: boolean;
  canEditRoles: (roleId: number | undefined) => boolean;
}

/**
 * A component to display an individual device in a list
 * @param props props of the component
 * @param props.device an object to represent a device
 */
function RoleTile(props: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [roleSelected, setRoleSelected] = useState<UserRoleInfo>();
  const [submitted, setSubmitted] = useState(false);

  const getAvatarText = () => {
    if (props?.party?.__typename === 'User') {
      const first: string | undefined = props?.party?.firstName?.charAt(0);
      const last: string | undefined = props?.party?.lastName?.charAt(0);
      if (first && last) return first + last;
      return 'U';
    }
    return 'G';
  };

  const getListText = () => {
    if (props?.party?.__typename === 'User') {
      const first: string | undefined = props?.party?.firstName;
      const last: string | undefined = props?.party?.lastName;
      const email: string | undefined = props?.party?.email;
      if (first && last) {
        return {
          primary: `${first} ${last}`,
          secondary: email || props?.party?.login?.toLowerCase(),
        };
      }
      return {
        primary: 'Inactive User',
        secondary: email || props?.party?.login?.toLowerCase(),
      };
    }
    if (props?.party?.__typename === 'Group') {
      const email: string | undefined = props?.party?.email;
      if (props?.party?.name) {
        return { primary: props?.party?.name, secondary: email || 'AD Group' };
      }
      return { primary: 'Group name missing', secondary: email || 'AD Group' };
    }
    return { primary: 'Not Available', secondary: 'NA' };
  };

  const handleOpen = (displayName: UserRoleInfo) => {
    setRoleSelected(displayName);
    setOpen(true);
  };

  const handleClose = () => {
    setSubmitted(false);
    setOpen(false);
  };

  const getRoles = () => {
    return props?.roles?.map((role: UserRoleInfo) => {
      if (
        role.roleId !== RoleId.FunctionalAreaRole &&
        props?.canEditRoles(role.roleId)
      ) {
        return (
          <DevHubTooltip
            hidden={!role?.description}
            title={role?.description ? role?.description : ''}
          >
            <DevHubChip
              chipSize="medium"
              key={role.displayName}
              label={role.displayName}
              onDelete={
                props.showDeleteIcon ? () => handleOpen(role) : undefined
              }
            />
          </DevHubTooltip>
        );
      }
      return (
        <DevHubTooltip
          hidden={!role?.description}
          title={role?.description ? role?.description : ''}
        >
          <DevHubChip
            chipSize="medium"
            key={role.displayName}
            label={role.displayName}
            disabled={props.showDisabled}
          />
        </DevHubTooltip>
      );
    });
  };

  return (
    <DevHubPaper
      slim
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <DevHubGrid
        container
        padding="small"
        alignItems="center"
        justifyContent="flex-start"
      >
        <DevHubGrid item>
          <DevHubAvatar>
            <DevHubTypography fontColor="white">
              {getAvatarText()}
            </DevHubTypography>
          </DevHubAvatar>
        </DevHubGrid>
        <DevHubGrid item>
          <DevHubListItemText
            primary={getListText()?.primary}
            secondary={<div>{getListText()?.secondary}</div>}
          />
        </DevHubGrid>
      </DevHubGrid>
      <DevHubGrid container padding="medium" alignItems="center">
        <DevHubGrid item>{getRoles()}</DevHubGrid>
      </DevHubGrid>
      <DeleteResponsibility
        appId={props.appId}
        party={props?.party}
        displayName={roleSelected?.displayName!}
        open={open}
        handleOpen={() => handleOpen(roleSelected!)}
        handleClose={handleClose}
        responsibilityId={roleSelected?.responsibilityId!}
        rolesQuery={props.rolesQuery}
        rolesVariables={props.rolesVariables}
        setSubmitted={setSubmitted}
        submitted={submitted}
      />
    </DevHubPaper>
  );
}

export default RoleTile;
